/**
 * Handlers for bulk operations on multiple products
 */

export const createBulkOperationHandlers = (
  products,
  setProducts,
  localEdits,
  setLocalEdits, 
  selectedProductIds,
  setHasUnsavedChanges, 
  showToast,
  setIsPriceUpdating, 
  setIsInventoryUpdating, 
  setIsProductUpdating
) => {
  /**
   * Handle bulk price changes for selected products
   */
  const handleBulkPriceChange = (priceChange, priceChangeType, selectedProductIds, products) => {
    console.log('Handling bulk price change:', { priceChange, priceChangeType, selectedCount: selectedProductIds.size });
    
    if (!priceChange || selectedProductIds.size === 0) return;

    try {
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };

        selectedProductIds.forEach(productId => {
          const product = products.find(p => p.id.toString() === productId.toString());
          if (!product || !product.variants) return;

          // Initialize product in localEdits if needed
          if (!updatedEdits[productId]) {
            updatedEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
          }
          if (!updatedEdits[productId].variant_updates) {
            updatedEdits[productId].variant_updates = {};
          }

          product.variants.forEach(variant => {
            if (!variant.price) return;

            // Initialize variant in localEdits if needed
            if (!updatedEdits[productId].variant_updates[variant.id]) {
              updatedEdits[productId].variant_updates[variant.id] = {};
            }

            // Get current values from localEdits or original variant
            const editedVariant = updatedEdits[productId].variant_updates[variant.id];
            const currentPrice = editedVariant.price !== undefined 
              ? parseFloat(editedVariant.price) 
              : parseFloat(variant.price);
            const currentComparePrice = editedVariant.compare_at_price !== undefined
              ? parseFloat(editedVariant.compare_at_price)
              : parseFloat(variant.compareAtPrice || 0);

            // Calculate new price based on the selected change type
            let newPrice = currentPrice;
            if (priceChangeType === 'increase') {
              newPrice = currentPrice * (1 + parseFloat(priceChange) / 100);
            } else if (priceChangeType === 'decrease') {
              newPrice = currentPrice * (1 - parseFloat(priceChange) / 100);
            } else if (priceChangeType === 'set') {
              newPrice = parseFloat(priceChange);
            }

            // Update the variant price in localEdits
            updatedEdits[productId].variant_updates[variant.id].price = newPrice.toFixed(2);
            
            // Update timestamp to force re-render
            updatedEdits[productId]._timestamp = Date.now();
          });
        });

        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error in handleBulkPriceChange:', error);
      showToast('Failed to update prices', true);
    }
  };

  /**
   * Handle rounding prices for selected products
   */
  const handleRoundPrices = (roundTo, selectedProductIds, products) => {
    console.log('Handling round prices:', { roundTo, selectedCount: selectedProductIds.size });
    
    if (!roundTo || selectedProductIds.size === 0) return;

    try {
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };

        selectedProductIds.forEach(productId => {
          const product = products.find(p => p.id.toString() === productId.toString());
          if (!product || !product.variants) return;

          // Initialize product in localEdits if needed
          if (!updatedEdits[productId]) {
            updatedEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
          }
          if (!updatedEdits[productId].variant_updates) {
            updatedEdits[productId].variant_updates = {};
          }

          product.variants.forEach(variant => {
            if (!variant.price) return;

            // Initialize variant in localEdits if needed
            if (!updatedEdits[productId].variant_updates[variant.id]) {
              updatedEdits[productId].variant_updates[variant.id] = {};
            }

            // Get current price from localEdits or original variant
            const editedVariant = updatedEdits[productId].variant_updates[variant.id];
            const currentPrice = editedVariant.price !== undefined 
              ? parseFloat(editedVariant.price) 
              : parseFloat(variant.price);

            // Calculate rounded price
            let roundedPrice = currentPrice;
            const roundToValue = parseFloat(roundTo);
            
            if (roundToValue > 0) {
              // Round down to nearest multiple of roundTo and then subtract 0.01
              roundedPrice = Math.floor(currentPrice / roundToValue) * roundToValue - 0.01;
              
              // Ensure price is not negative
              roundedPrice = Math.max(0, roundedPrice);
            }

            // Update the variant price in localEdits
            updatedEdits[productId].variant_updates[variant.id].price = roundedPrice.toFixed(2);
            
            // Update timestamp to force re-render
            updatedEdits[productId]._timestamp = Date.now();
          });
        });

        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error in handleRoundPrices:', error);
      showToast('Failed to round prices', true);
    }
  };

  /**
   * Handle price updates for specific variants
   */
  const handlePriceUpdate = (updates) => {
    console.log('Handling price updates:', updates);

    try {
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };

        updates.forEach(update => {
          const { productId, variantId, field, value } = update;

          // Initialize product in localEdits if needed
          if (!updatedEdits[productId]) {
            updatedEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
          }
          if (!updatedEdits[productId].variant_updates) {
            updatedEdits[productId].variant_updates = {};
          }

          // Initialize variant in localEdits if needed
          if (!updatedEdits[productId].variant_updates[variantId]) {
            updatedEdits[productId].variant_updates[variantId] = {};
          }

          // Update the specific field
          const fieldName = field === 'compareAtPrice' ? 'compare_at_price' : field;
          updatedEdits[productId].variant_updates[variantId][fieldName] = 
            value !== null ? value.toString() : null;
          
          // Update timestamp to force re-render
          updatedEdits[productId]._timestamp = Date.now();

          console.log('Updated variant in localEdits:', {
            productId,
            variantId,
            field: fieldName,
            value,
            variant: updatedEdits[productId].variant_updates[variantId]
          });
        });

        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
      showToast('Price updated successfully');
    } catch (error) {
      console.error('Error in price update process:', error);
      showToast('Failed to update price', true);
    }
  };

  /**
   * Handle inventory updates for specific variants
   */
  const handleInventoryUpdate = (updates) => {
    console.log('Handling inventory updates:', updates);
    if (setIsInventoryUpdating) {
      setIsInventoryUpdating(true);
    }

    try {
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };

        updates.forEach(update => {
          const { productId, variantId, updates: variantUpdates } = update;

          // Initialize product in localEdits if needed
          if (!updatedEdits[productId]) {
            updatedEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
          }
          if (!updatedEdits[productId].variant_updates) {
            updatedEdits[productId].variant_updates = {};
          }

          // Initialize variant in localEdits if needed
          if (!updatedEdits[productId].variant_updates[variantId]) {
            updatedEdits[productId].variant_updates[variantId] = {};
          }

          // Update all the specified fields
          Object.entries(variantUpdates).forEach(([field, value]) => {
            // Handle different value types safely
            let processedValue = '';
            if (value !== null && value !== undefined) {
              // For objects and values with toString method
              if (typeof value === 'object') {
                processedValue = JSON.stringify(value);
              } else {
                // For primitive values
                processedValue = String(value);
              }
            }
            updatedEdits[productId].variant_updates[variantId][field] = processedValue;
          });
          
          // Update timestamp to force re-render
          updatedEdits[productId]._timestamp = Date.now();
        });

        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
      showToast('Inventory updated successfully');
    } catch (error) {
      console.error('Error in inventory update process:', error);
      showToast('Failed to update inventory', true);
    } finally {
      if (setIsInventoryUpdating) {
        setIsInventoryUpdating(false);
      }
    }
  };

  /**
   * Handle product updates for specific fields
   */
  const handleProductUpdate = (updates) => {
    console.log('Handling product updates:', updates);
    if (setIsProductUpdating) {
      setIsProductUpdating(true);
    }

    try {
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };

        updates.forEach(update => {
          const { productId, field, value } = update;

          // Initialize product in localEdits if needed
          if (!updatedEdits[productId]) {
            updatedEdits[productId] = { _timestamp: Date.now() };
          }

          // Update the specific field
          updatedEdits[productId][field] = value;
          
          // Update timestamp to force re-render
          updatedEdits[productId]._timestamp = Date.now();
        });

        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
      showToast('Products updated successfully');
    } catch (error) {
      console.error('Error in product update process:', error);
      showToast('Failed to update products', true);
    } finally {
      if (setIsProductUpdating) {
        setIsProductUpdating(false);
      }
    }
  };

  /**
   * Handle bulk updates to metafields
   */
  const handleBulkUpdateMetafields = (updates) => {
    console.log('Handling metafield updates:', updates);
    setIsProductUpdating(true);

    try {
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };

        updates.forEach(update => {
          const { productId, field, value } = update;

          // Initialize product in localEdits if needed
          if (!updatedEdits[productId]) {
            updatedEdits[productId] = { _timestamp: Date.now() };
          }

          // Update the specific field
          updatedEdits[productId][field] = value;
          
          // Update timestamp to force re-render
          updatedEdits[productId]._timestamp = Date.now();
        });

        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
      showToast('Metafields updated successfully');
    } catch (error) {
      console.error('Error in metafield update process:', error);
      showToast('Failed to update metafields', true);
    } finally {
      setIsProductUpdating(false);
    }
  };

  /**
   * Handle changes to price tools settings
   */
  const handlePriceToolsChange = (field, value) => {
    console.log(`handlePriceToolsChange: Field ${field}, Value:`, value);
    
    return { field, value };
  };

  return {
    handleBulkPriceChange,
    handleRoundPrices,
    handlePriceUpdate,
    handleInventoryUpdate,
    handleProductUpdate,
    handleBulkUpdateMetafields,
    handlePriceToolsChange
  };
};
